import { useCallback, useId, useState, type MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { Swiper as SwiperType } from 'swiper';
import cn from 'classnames';

import Carousel, { useCarousel } from '_components/Carousel';
import LinkButton from '_components/UI/LinkButton';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import {
  trackCategoryNavClick,
  type CategoryNavigationLocation,
} from '_utils/GA4/trackCategoryNavigationEvent';

import CategoryTile from './CategoryTile';
import type {
  CategoryNavigationLayout,
  CategoryNavigationShopAll,
  CategoryNavigationTile,
} from './types';
import styles from './CategoryNavigationTiles.module.scss';

export interface CategoryNavigationTilesProps {
  /** Section title, e.g. "Ready, Set, Camp". Rendered as the `<h2>`. */
  heading: string;
  /** Optional link to the parent category. Hidden when absent. */
  shopAll?: CategoryNavigationShopAll;
  items: CategoryNavigationTile[];
  /** Defaults to `grid` (CIP + CLP). */
  layout?: CategoryNavigationLayout;
  /**
   * The page type, reported as `interaction_location` on the GA4
   * `click_cat_nav` hit (ED-425), which the section sends itself.
   */
  interactionLocation: CategoryNavigationLocation;
  /**
   * Called on every tile click before navigation, for host-specific side
   * effects. Calling `preventDefault` suppresses the section's deferred
   * navigation, leaving the host to handle routing.
   */
  onItemClick?: (
    item: CategoryNavigationTile,
    index: number,
    event: MouseEvent<HTMLAnchorElement>
  ) => void;
  /** Called when the SHOP ALL link is clicked, for the same purpose. */
  onShopAllClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Drops the section's own side gutters, for hosts that already sit inside a
   * padded page container (the PLP header slot above the results).
   */
  contained?: boolean;
  /**
   * Preloads the first few tile images. Only for hosts that know the section
   * renders above the fold; everywhere else the images lazy load.
   */
  priority?: boolean;
}

/**
 * Tiles preloaded when `priority` is set: the first row on mobile (three
 * across). Preloading a full desktop row of six is more weight than it earns.
 */
const PRIORITY_TILE_COUNT = 3;

type AnchorMouseEvent = MouseEvent<HTMLAnchorElement>;

interface LinkHandlers {
  onClick: (event: AnchorMouseEvent) => void;
  onAuxClick: (event: AnchorMouseEvent) => void;
}

interface ShopAllLinkProps extends LinkHandlers {
  href: string;
  label: string;
  className?: string;
}

const ShopAllLink = ({
  href,
  label,
  className,
  onClick,
  onAuxClick,
}: ShopAllLinkProps) => (
  <div className={className}>
    <LinkButton
      href={href}
      variant="black"
      onClick={onClick}
      onAuxClick={onAuxClick}
    >
      {label}
    </LinkButton>
  </div>
);

// ─── Grid layout ──────────────────────────────────────────────────────────────

interface LayoutProps {
  heading: string;
  headingId: string;
  /**
   * SHOP ALL with its rendered label (authored or the localized default) and
   * its click handlers.
   */
  shopAll?: Omit<ShopAllLinkProps, 'className'>;
  items: CategoryNavigationTile[];
  priority: boolean;
  tileHandlers: (item: CategoryNavigationTile, index: number) => LinkHandlers;
}

/**
 * Figma "Maximum amount (Vertical)". Fixed column counts (3 on mobile, 6 on
 * desktop) so a short last row keeps its tiles in their own slots instead of
 * stretching or re-centring them. Every tile shows at every breakpoint.
 */
function GridLayout({
  heading,
  headingId,
  shopAll,
  items,
  priority,
  tileHandlers,
}: LayoutProps) {
  return (
    <div className={styles.gridLayout}>
      <h2 id={headingId} className={cn(styles.heading, styles.gridHeading)}>
        {heading}
      </h2>

      {/* One link, placed by grid area: beside the heading on desktop, under
          the tiles on mobile. */}
      {shopAll && <ShopAllLink {...shopAll} className={styles.gridShopAll} />}

      <ul className={styles.grid}>
        {items.map((item, index) => (
          <li key={`${item.label}-${item.href}`} className={styles.gridItem}>
            <CategoryTile
              {...item}
              priority={priority && index < PRIORITY_TILE_COUNT}
              {...tileHandlers(item, index)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Carousel layout (needs Carousel context) ─────────────────────────────────

/**
 * Figma "Carousel (Horizontal)". Arrows are desktop-only and dropped when
 * every tile already fits; mobile scrolls by touch. SHOP ALL is not in the
 * Figma carousel, but when authored it sits in the header beside the arrows.
 */
function CarouselLayout({
  heading,
  headingId,
  shopAll,
  items,
  priority,
  tileHandlers,
}: LayoutProps) {
  const { t } = useTranslation('common');
  const {
    state: { isScrollable },
  } = useCarousel();

  // Empty until the strip moves, so screen readers don't announce a position
  // on page load before the user has done anything.
  const [statusMessage, setStatusMessage] = useState('');

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      setStatusMessage(
        t('categoryNavigationTiles.slideStatus', {
          current: swiper.activeIndex + 1,
          total: items.length,
        })
      );
    },
    [items.length, t]
  );

  return (
    <>
      <div className={styles.carouselHeader}>
        <h2 id={headingId} className={styles.heading}>
          {heading}
        </h2>

        {(shopAll || isScrollable) && (
          <div className={styles.actions}>
            {shopAll && <ShopAllLink {...shopAll} />}
            {isScrollable && (
              <div className={styles.buttonGroup}>
                <Carousel.NavPrev
                  aria-label={t('categoryNavigationTiles.prev')}
                  iconSize={16}
                />
                <Carousel.NavNext
                  aria-label={t('categoryNavigationTiles.next')}
                  iconSize={16}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className={styles.track}
        role="group"
        aria-roledescription={t('categoryNavigationTiles.carousel')}
        aria-label={heading}
      >
        <Carousel.Track
          className={styles.swiper}
          spaceBetween={2}
          wrapperTag="ul"
          onSlideChange={handleSlideChange}
        >
          {items.map((item, index) => (
            <Carousel.Slide
              key={`${item.label}-${item.href}`}
              tag="li"
              className={styles.slide}
            >
              <CategoryTile
                {...item}
                priority={priority && index < PRIORITY_TILE_COUNT}
                {...tileHandlers(item, index)}
              />
            </Carousel.Slide>
          ))}
        </Carousel.Track>
      </div>

      <p className={styles.status} role="status" aria-live="polite">
        {statusMessage}
      </p>
    </>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

/**
 * ED-218: Category Navigation Tiles.
 *
 * A titled set of Product Category Tiles linking to sub-categories, with an
 * optional SHOP ALL link to the parent category. A grid on the CIP and the
 * CLP, a carousel on the PLP and the homepage. Content comes entirely through
 * props so every page can share it.
 *
 * Rendered as a `<nav>` landmark named by its own `<h2>`, with the tiles in a
 * `<ul>` in both layouts.
 */
const CategoryNavigationTiles = ({
  heading,
  shopAll,
  items,
  layout = 'grid',
  interactionLocation,
  onItemClick,
  onShopAllClick,
  contained = false,
  priority = false,
}: CategoryNavigationTilesProps) => {
  const { t } = useTranslation('common');
  const { asPath } = useRouter();
  const headingId = useId();

  if (!heading || !items?.length) return null;

  // ED-425: `click_cat_nav`. Query string dropped so a section aggregates into
  // one row in reporting whatever filters the visitor arrived with.
  const linkHandlers = (
    link: { href: string; text: string; index: number | null },
    hostOnClick?: (event: AnchorMouseEvent) => void
  ): LinkHandlers => {
    const data = {
      interactionLocation,
      eventPage: asPath.split('?')[0],
      linkUrl: link.href,
      linkText: link.text,
      heading,
      index: link.index,
    };

    return {
      onClick: (event) => {
        hostOnClick?.(event);

        // A modified click keeps this page alive, and a host that already
        // prevented the default handles routing itself: neither needs the
        // navigation deferred.
        if (isModifiedClick(event) || event.defaultPrevented) {
          trackCategoryNavClick({ data });
          return;
        }

        event.preventDefault();
        trackCategoryNavClick({
          data,
          eventCallback: () => {
            window.location.href = link.href;
          },
        });
      },
      // A middle click fires `auxclick`, not `click`. Track it and leave the
      // browser's new-tab navigation alone; other buttons (e.g. right click for
      // the context menu) aren't a click on the link.
      onAuxClick: (event) => {
        if (event.button !== 1) return;
        trackCategoryNavClick({ data });
      },
    };
  };

  let shopAllLink: LayoutProps['shopAll'];
  if (shopAll) {
    const label = shopAll.label || t('categoryNavigationTiles.shopAll');
    shopAllLink = {
      href: shopAll.href,
      label,
      ...linkHandlers(
        { href: shopAll.href, text: label, index: null },
        onShopAllClick
      ),
    };
  }

  const layoutProps: LayoutProps = {
    heading,
    headingId,
    shopAll: shopAllLink,
    items,
    priority,
    tileHandlers: (item, index) =>
      linkHandlers(
        { href: item.href, text: item.label, index },
        onItemClick && ((event) => onItemClick(item, index, event))
      ),
  };

  return (
    <nav
      className={cn(styles.root, { [styles.contained]: contained })}
      aria-labelledby={headingId}
      data-testid="category-navigation-tiles"
      data-layout={layout}
    >
      {layout === 'carousel' ? (
        <div className={styles.carouselInner}>
          <Carousel.Provider>
            <CarouselLayout {...layoutProps} />
          </Carousel.Provider>
        </div>
      ) : (
        <GridLayout {...layoutProps} />
      )}
    </nav>
  );
};

export default CategoryNavigationTiles;
